import { matchesFilter } from './cardFilter';
import { anchorCoveredCells } from '../core/auraPlacement';
import { hasKeyword, isCardSilenced } from '../core/keywordQuery';

// ── Unified effective-status helper (v1.16a) ─────────────────────
//
// One source of truth for "which statuses does this card have right
// now." Five sources combine:
//   • card.statuses. Manually toggled via the status ring
//   • card._auraStatuses. Granted by another card's static aura
//   • card._printedKeywords. Baseline-printed on the card
//   • card._suppressedKeywords. DENY list (player-toggled, persistent)
//   • card._auraSuppressedKeywords. DENY list from aura REVOKE_KEYWORD
//
// The player's deny list lets them turn OFF a printed keyword via the
// radial menu (Airborne on Ancient Dragon, etc.). Without it, the
// union would just re-merge the printed keyword right back in.
// Suppressions clear when the card leaves the field so re-entering
// gets a fresh keyword set.
//
// The aura deny list (`_auraSuppressedKeywords`) is the REVOKE_KEYWORD
// op's storage. Rebuilt fresh on every recomputeStaticAuras pass so a
// source leaving play / losing scope auto-restores the keyword.
// Entangle Terrain: "Minions occupying affected sites lose Airborne."
//
// The 'disabled' status is a special override: when a card is disabled
// it loses every keyword (manual / aura / printed alike), keeping only
// 'disabled' itself. Pure. Safe to call from any context.

const DISABLED_KEY = 'disabled';
const SILENCED_KEY = 'silenced';

export function getEffectiveStatuses(card, { includePrinted = true } = {}) {
  if (!card) return [];
  const suppressed = new Set([
    ...(card._suppressedKeywords || []),
    ...(card._auraSuppressedKeywords || []),
  ]);
  const manual  = card.statuses        || [];
  const auraAll = card._auraStatuses   || [];
  const printedAll = includePrinted ? (card._printedKeywords || []) : [];
  // Suppression only subtracts from "automatic" sources. Printed
  // and aura. The player's own manual toggles aren't a deny target;
  // they're the WAY the player expresses suppression in the first place.
  const aura    = auraAll.filter((k) => !suppressed.has(k));
  // 'lance' is a printed keyword used only as the on-entry grant signal.
  // the actual Lance is an equipment token. Its badge comes from
  // displayStatusKeys (reads equippedCards) so it disappears when the
  // Lance breaks; drop it here so the printed keyword can't leak a
  // permanent lance status.
  const printed = printedAll.filter((k) => !suppressed.has(k) && k !== 'lance');
  const union = new Set([...manual, ...aura, ...printed]);
  if (union.has(DISABLED_KEY)) return [DISABLED_KEY];
  // Silence: codex "A silenced card loses all its special abilities."
  // Its keyword abilities drop from the effective set (badges, radial,
  // any effective-status consumer); only the silenced state remains,
  // mirroring the disabled override. Reverses cleanly when silence ends.
  // 'flooded' survives. It's the site's water-site characteristic,
  // co-granted with the silence by Atlantean Fate (see keywordQuery).
  if (union.has(SILENCED_KEY)) {
    return union.has('flooded') ? [SILENCED_KEY, 'flooded'] : [SILENCED_KEY];
  }
  return [...union];
}

export function hasEffectiveStatus(card, status) {
  return getEffectiveStatuses(card).includes(status);
}

/**
 * Smart status toggle that survives printed and aura-granted sources.
 *
 * Mutates the card so its effective status set ends up containing
 * `key` iff `active === true`. Strategy:
 *
 *   • Turning ON. Remove `key` from `_suppressedKeywords` (un-deny).
 *                    If still not active (no printed / aura grant),
 *                    push onto manual statuses.
 *   • Turning OFF. Remove `key` from manual statuses. If still
 *                    active (printed or aura keeps re-granting it),
 *                    push onto `_suppressedKeywords` to deny it.
 *
 * Returns true when the card's effective set actually changed so
 * callers can skip broadcasts / rebuilds on no-ops.
 */
export function applyStatusToggle(card, key, active) {
  if (!card) return false;
  if (!card.statuses) card.statuses = [];
  if (!card._suppressedKeywords) card._suppressedKeywords = [];

  const before = getEffectiveStatuses(card).includes(key);

  if (active) {
    if (card._suppressedKeywords.includes(key)) {
      card._suppressedKeywords = card._suppressedKeywords.filter((s) => s !== key);
    }
    const hasPrinted = (card._printedKeywords || []).includes(key);
    const hasAura    = (card._auraStatuses    || []).includes(key);
    // Ward/Stealth are TOKEN keywords: protection reads card.statuses only
    // (hasActiveWard / hasActiveStealth), never the printed keyword. Merely
    // lifting the suppression on a consumed-token card lit the badge without
    // restoring protection, so a re-grant must always mint the real token.
    // Never on a disabled/silenced card: it can't hold the token (codex),
    // the effective set stays ['disabled'] so the caller sees a no-op and
    // never broadcasts, and the silently-armed local token would surface as
    // a this-client-only ward when the disable lifts.
    const tokenKeyword = (key === 'ward' || key === 'stealth')
      && !isDisabled(card) && !isCardSilenced(card);
    if ((tokenKeyword || (!hasPrinted && !hasAura)) && !card.statuses.includes(key)) {
      card.statuses.push(key);
    }
  } else {
    if (card.statuses.includes(key)) {
      card.statuses = card.statuses.filter((s) => s !== key);
    }
    const hasPrinted = (card._printedKeywords || []).includes(key);
    const hasAura    = (card._auraStatuses    || []).includes(key);
    if ((hasPrinted || hasAura) && !card._suppressedKeywords.includes(key)) {
      card._suppressedKeywords.push(key);
    }
  }

  return before !== getEffectiveStatuses(card).includes(key);
}

// Card lifecycle reset moved to cardEffects/lifecycle.js. Kept here
// as a re-export so existing imports (`{ resetCardEntryState }`) keep
// working through the legacy alias.
export { resetCardOnLeavePlay as resetCardEntryState } from '../core/lifecycle';

// ── Shared cell-scope helper (v1.18a) ────────────────────────────
//
// One source of truth for how a positional scope keyword maps to a
// set of grid cells, relative to a source cell. Used by:
//   - the aura/target scope resolver (resolveAuraScope below)
//   - GameBoard's cell-pick UI (_startCellTargeting for damage_each_at_location)
//   - GameBoard's token-summon (_summonTokenAtLocations for Midland Army)
//
// Sorcery semantics (per the official rulebook):
//   'here'     → the source cell itself.
//   'nearby'   → 9 cells: the source plus 8 surrounding (Chebyshev ≤ 1).
//                A card is always nearby its own location.
//   'adjacent' → 5 cells: the source plus 4 orthogonal cardinal
//                neighbors (no diagonals). The source counts; corner
//                cells naturally have fewer adjacent cells.
//
// Distance-based scopes ("up to N steps away") aren't included here.
// they need site-gated pathing which lives in GameBoard.
//
// Returns a Set<'col,row'>.

export function cellsInScope(sourceCell, scope, totalCols, totalRows) {
  const out = new Set();
  if (!sourceCell) return out;
  for (let c = 0; c < totalCols; c++) {
    for (let r = 0; r < totalRows; r++) {
      const dx = Math.abs(c - sourceCell.col);
      const dy = Math.abs(r - sourceCell.row);
      if (scope === 'here') {
        if (dx === 0 && dy === 0) out.add(`${c},${r}`);
      } else if (scope === 'nearby') {
        if (dx <= 1 && dy <= 1) out.add(`${c},${r}`);
      } else if (scope === 'adjacent') {
        // Source cell + 4 cardinal neighbors. dx+dy ≤ 1 captures both
        // (0,0) and the four (±1,0)/(0,±1) tiles, excluding diagonals.
        if (dx + dy <= 1) out.add(`${c},${r}`);
      }
    }
  }
  return out;
}

export function isDisabled(card) {
  // Same precedence as getEffectiveStatuses (the badge): a manual token
  // always disables; a printed or aura grant is deniable through either
  // suppression list. hasKeyword keeps 'disabled' silence-exempt, so every
  // engine gate (defend / intercept / tap / move) reads one predicate.
  return hasKeyword(card, DISABLED_KEY);
}

// Aura scope resolution. Pure functions. No React, no Three.js, no
// mutable game state. GameBoard provides the iterable of meshes and a
// cell-lookup function; everything else is computed here.
//
// Two scopes use the grid: 'nearby' (8-neighbor adjacency including
// diagonals) and 'here' (same cell). 'global' ignores position. Other
// scopes return self_controller targets. Non-positional.

import { getCellAt } from '../../grid';

// Resolve a world-space point to a grid {col, row}. Wraps grid.getCellAt
// so callers don't need to import the grid module directly.
export function cellAtWorld(grid, x, z) {
  if (!grid) return null;
  return getCellAt(grid, x, z);
}

/**
 * Find every cardInstance that matches an aura's scope + filter.
 *
 * @param {Object} sourceCard - the card whose aura is firing (cardInstance shape).
 * @param {Object} scope - { where: 'nearby'|'global'|'here'|... }.
 * @param {Object} args - { filter: { controller?, type?, subtype? }, exclude_self? }.
 * @param {Object} deps
 * @param {Iterable<[any, { userData: { cardInstance }, position: { x, z } }]>} deps.meshes
 *   GameBoard.meshes Map or any compatible iterable.
 * @param {(x: number, z: number) => { col: number, row: number } | null} deps.cellAt
 *   Resolves a world-space (x, z) to a grid cell. Wrap cellAtWorld with your current grid.
 * @param {Object} [deps.overrides]
 * @param {{ x: number, z: number }} [deps.overrides.sourcePos]
 *   Used for equipped artifacts whose own mesh isn't on the table. Scope
 *   is resolved relative to the host's position instead.
 * @param {boolean} [deps.overrides.controllerRotated]
 *   Overrides the source's rotated flag for the 'self' / 'enemy' controller filter.
 *   For an equipped artifact, this is the host's side.
 *
 * @returns {Array<Object>} affected cardInstances.
 */
export function resolveAuraScope(sourceCard, scope, args, { meshes, cellAt, getFullCard = null, overrides = {} }) {
  const where = scope?.where;
  const filter = args?.filter || {};
  const excludeSelf = args?.exclude_self !== false;
  const effectiveRotated = overrides.controllerRotated !== undefined
    ? overrides.controllerRotated
    : sourceCard.rotated;

  // 'self'. The source card only (SCOPE_WHERE.SELF). Effects whose
  // target IS the source (add_name_alias "Counts as a Grey Wolves",
  // grant_keyword unmovable on Talamh Dreig) resolve here. The
  // exclude_self default exists to keep a source out of AREA scopes;
  // applying it to 'self' would make the scope always empty, so it is
  // deliberately ignored.
  if (where === 'self') {
    const ctx = { source: sourceCard, effectiveRotated, hasEffectiveStatus, getFullCard };
    return matchesFilter(sourceCard, filter, ctx) ? [sourceCard] : [];
  }

  let sourceCell = null;
  if (where === 'nearby' || where === 'here' || where === 'adjacent') {
    if (overrides.sourcePos) {
      sourceCell = cellAt(overrides.sourcePos.x, overrides.sourcePos.z);
    } else {
      const sourceMesh = findMeshById(meshes, sourceCard.id);
      if (sourceMesh) sourceCell = cellAt(sourceMesh.position.x, sourceMesh.position.z);
    }
  }

  // 'covered'. The source aura's actual occupancy. Reads the stamped
  // anchor + shape from the card (set at addCardToTable). Returns the
  // set of cells; the per-card loop below tests `dx/dy` against this
  // set rather than a single source cell.
  let coveredCellKeys = null;
  if (where === 'covered') {
    const anchor = sourceCard?._auraAnchor;
    const shape = sourceCard?._auraShape?.shape;
    if (anchor && shape) {
      coveredCellKeys = new Set(anchorCoveredCells(anchor, shape));
    }
  }

  // Per-card filtering routes through the canonical `matchesFilter`.
  // every field (type, subtype/s, name, name_words, element, status,
  // controller, allied/enemy, summoned_this_turn, currently_tapped,
  // composites) is owned by one module so adding a new field unlocks
  // every consumer at once.
  const filterCtx = {
    source: sourceCard,
    effectiveRotated,
    hasEffectiveStatus,
    getFullCard,
    meshes,
    cellAt,
  };
  // Is a mesh-card's cell inside this scope's geometry? Independent of
  // the filter so a bearer that fails the filter (e.g. an Avatar in a
  // Minion-or-Artifact scope) can still expose its carried cards below.
  const cellInScope = (cell) => {
    if (where === 'global') return true;
    if (!cell) return false;
    if (where === 'covered') {
      return !!coveredCellKeys && coveredCellKeys.size > 0
        && coveredCellKeys.has(`${cell.col},${cell.row}`);
    }
    if (where === 'nearby' || where === 'here' || where === 'adjacent') {
      if (!sourceCell) return false;
      const dx = Math.abs(cell.col - sourceCell.col);
      const dy = Math.abs(cell.row - sourceCell.row);
      // 'here'. Same cell. 'nearby'. 8-neighbors + own cell. 'adjacent'
      // Own cell + 4 cardinal (a middle Square has 5 adjacent squares).
      if (where === 'here') return dx === 0 && dy === 0;
      if (where === 'nearby') return dx <= 1 && dy <= 1;
      if (where === 'adjacent') return dx + dy <= 1;
    }
    return false;
  };

  // Codex "Nearby"/"Adjacent": references to nearby/adjacent SITES, VOIDS or
  // SQUARES may cross regional boundaries, but references to nearby/adjacent
  // MINIONS / ARTIFACTS / LOCATIONS may NOT (Belfry on a site can't untap an
  // ally in a nearby void). So an area UNIT grant (Horn of Caerleon's Charge,
  // House Arn Bannerman's +1) must not reach a burrowed/submerged unit at a
  // nearby cell. Only same-region units. 'here' is exempt: a site using 'here'
  // deliberately spans its own surface + subsurface (Dome of Osiros). Sites
  // themselves are the location layer and always pass (they cross regions).
  const regionOf = (c) => {
    const s = c?.subsurface;
    return s === 'underground' || s === 'underwater' ? s : 'surface';
  };
  const regionGated = where === 'nearby' || where === 'adjacent';
  const sourceRegion = regionOf(overrides.bearerCard || sourceCard);
  const sameRegion = (c) => !regionGated || c?.isSite || regionOf(c) === sourceRegion;

  const targets = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    // Per-card mesh lets cell-aware filters (region 'void', cells)
    // resolve this card's board square via cellAt.
    const cell = where === 'global' ? null : cellAt(mesh.position.x, mesh.position.z);
    if (!cellInScope(cell)) continue;

    if (!(excludeSelf && card.id === sourceCard.id)
        && sameRegion(card)
        && matchesFilter(card, filter, { ...filterCtx, cardMesh: mesh })) {
      targets.push(card);
    }

    // Carried cards (equipped artifacts, carried units) share their
    // bearer's square. They are NEVER off the table for another card's
    // effects. A location scan can't see them (no own mesh), so surface
    // each one here, evaluated at the bearer's cell and inheriting the
    // bearer's region. Tag the carrier so a mutating op can detach it
    // (or recognise a ride-along when the bearer is also affected).
    const carried = card.equippedCards;
    if (carried && carried.length) {
      for (const eq of carried) {
        // Equipped objects carry `instanceId` (server) or `id` (client render);
        // normalise so scope self-exclusion and the derived-bag lookup (keyed by
        // `.id`) resolve a carried artifact identically to a board card.
        if (eq.id == null && eq.instanceId != null) eq.id = eq.instanceId;
        if (excludeSelf && eq.id === sourceCard.id) continue;
        const probe = eq.subsurface == null && card.subsurface != null
          ? { ...eq, subsurface: card.subsurface }
          : eq;
        if (sameRegion(probe) && matchesFilter(probe, filter, { ...filterCtx, cardMesh: mesh })) {
          eq._carrierId = card.id;
          targets.push(eq);
        }
      }
    }
  }
  return targets;
}

// Find a mesh by its cardInstance id within an iterable of [_, mesh]
// pairs. Used internally for the default sourcePos lookup.
function findMeshById(meshes, id) {
  for (const [, mesh] of meshes) {
    if (mesh?.userData?.cardInstance?.id === id) return mesh;
  }
  return null;
}
